import { DetectorOptions } from 'i18next-browser-languagedetector';
import Cookie from 'js-cookie';

import { getDefaultLanguageByHostname, isValidLanguage } from '../text/translation';

//
// https://github.com/i18next/i18next-browser-languageDetector#interface
//

const getUrlLang = (): string | null => new URLSearchParams(window.location.search).get('lang');

export default {
  name: 'googleTranslateCookie',

  /**
   * Returns the active language, checked in priority order:
   * 1. #googtrans(<lang>) URL hash (Google Translate anchor)
   * 2. googtrans cookie (persisted user selection)
   * 3. ?lang= query parameter (shared links, direct navigation)
   * 4. Hostname-based default
   */
  lookup(options: DetectorOptions) {
    // 1. Hash — Google Translate navigation link sets this before the cookie is updated.
    const hash = window.location.hash;
    let groups = /^#googtrans\((.+?)\)$/.exec(hash ?? '');
    if (groups) {
      const lng = decodeURIComponent(groups[1]);
      if (isValidLanguage(lng)) {
        return lng;
      }
    }

    // 2. Cookie — saved user preference; always beats a shared-link ?lang= param.
    groups = /^\/.{2}\/(.{2})$/.exec(Cookie.get('googtrans') ?? '');
    if (groups) {
      const cookieLang = groups[1];
      if (isValidLanguage(cookieLang)) {
        return cookieLang;
      }
    }

    // 3. ?lang= — shared-link hint, only reaches here when no cookie exists.
    const urlLang = getUrlLang();
    if (urlLang !== null) {
      return isValidLanguage(urlLang) ? urlLang : 'en';
    }

    // 4. Hostname default.
    return getDefaultLanguageByHostname();
  },

  /**
   * Persists the active language to the googtrans cookie so it survives page reloads.
   * Skipped when the language originates from a ?lang= URL parameter — that selection is
   * intentionally session-only (shared links should not overwrite the user's saved preference).
   */
  cacheUserLanguage(lng: string, options: DetectorOptions) {
    if (getUrlLang() !== null) {
      return;
    }
    if (isValidLanguage(lng)) {
      Cookie.set('googtrans', `/en/${lng}`, { expires: 365 });
    }
  },
};
